import { useEffect, useMemo, useState } from 'react'
import {Star, TrendingUp, Calendar, Building2, ChevronDown, ChevronUp} from 'lucide-react'
import ColaboradorLayout from '@/components/ColaboradorLayout'
import { useEvaluations, type Evaluation } from '@/hooks/useEvaluations'
import { EVALUATION_ASPECTS } from '@/lib/evaluationAspects'
import { useAuth } from '@/hooks/useAuth'

function scoreColor(value: number) {
  if (value >= 8) return 'text-emerald-600'
  if (value >= 6) return 'text-amber-600'
  return 'text-red-600'
}

function barColor(value: number) {
  if (value >= 8) return 'bg-emerald-500'
  if (value >= 6) return 'bg-amber-500'
  return 'bg-red-500'
}

function EvaluationCard({ ev }: { ev: Evaluation }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 p-4 text-left"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
            <Building2 size={20} />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900">{ev.companyName || 'Empresa'}</p>
            <p className="flex items-center gap-1.5 text-xs text-slate-500">
              <Calendar size={12} /> Periodo {ev.period}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className={`text-xl font-bold ${scoreColor(ev.average)}`}>{ev.average.toFixed(1)}</p>
            <p className="text-[11px] text-slate-400">promedio</p>
          </div>
          {open ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
        </div>
      </button>

      {open ? (
        <div className="border-t border-slate-100 p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            {EVALUATION_ASPECTS.map((aspect) => {
              const value = Number(ev.scores?.[aspect.key]) || 0
              return (
                <div key={aspect.key}>
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span className="text-slate-600">{aspect.label}</span>
                    <span className={`font-semibold ${scoreColor(value)}`}>{value}/10</span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                    <div className={`h-full ${barColor(value)}`} style={{ width: `${value * 10}%` }} />
                  </div>
                </div>
              )
            })}
          </div>

          {(ev.comments || ev.observations || ev.recommendations) ? (
            <div className="mt-4 space-y-2 border-t border-slate-100 pt-4 text-sm">
              {ev.comments ? (
                <p className="text-slate-600"><span className="font-semibold text-slate-800">Comentarios:</span> {ev.comments}</p>
              ) : null}
              {ev.observations ? (
                <p className="text-slate-600"><span className="font-semibold text-slate-800">Observaciones:</span> {ev.observations}</p>
              ) : null}
              {ev.recommendations ? (
                <p className="text-slate-600"><span className="font-semibold text-slate-800">Recomendaciones:</span> {ev.recommendations}</p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function ColaboradorEvaluaciones() {
  const { user } = useAuth()
  const { evaluations, loading, fetchByCollaborator } = useEvaluations()

  useEffect(() => {
    if (user?.userId) {
      fetchByCollaborator(user.userId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId])

  const globalAverage = useMemo(() => {
    if (evaluations.length === 0) return 0
    const sum = evaluations.reduce((acc, e) => acc + (e.average || 0), 0)
    return Math.round((sum / evaluations.length) * 10) / 10
  }, [evaluations])

  const bestAspect = useMemo(() => {
    if (evaluations.length === 0) return null
    const totals: Record<string, { sum: number; count: number }> = {}
    evaluations.forEach((e) => {
      EVALUATION_ASPECTS.forEach((a) => {
        const v = Number(e.scores?.[a.key]) || 0
        if (!totals[a.key]) totals[a.key] = { sum: 0, count: 0 }
        totals[a.key].sum += v
        totals[a.key].count += 1
      })
    })
    let best: { label: string; avg: number } | null = null
    EVALUATION_ASPECTS.forEach((a) => {
      const t = totals[a.key]
      if (!t || t.count === 0) return
      const avg = t.sum / t.count
      if (!best || avg > best.avg) best = { label: a.label, avg }
    })
    return best
  }, [evaluations])

  return (
    <ColaboradorLayout
      title="Mis evaluaciones"
      subtitle="Consulta las calificaciones que recibes de tus empleadores y tu evolución profesional."
    >
      {loading ? (
        <div className="py-20 text-center text-sm text-slate-500">Cargando evaluaciones...</div>
      ) : evaluations.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center">
          <Star size={40} className="mx-auto text-slate-300" />
          <p className="mt-4 text-sm font-medium text-slate-700">Aún no tienes evaluaciones</p>
          <p className="mt-1 text-sm text-slate-500">
            Cuando una empresa con la que estés vinculado te evalúe, verás aquí tus calificaciones.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2 text-slate-500">
                <Star size={16} className="text-amber-500" />
                <span className="text-xs font-medium">Promedio general</span>
              </div>
              <p className={`mt-2 text-3xl font-bold ${scoreColor(globalAverage)}`}>{globalAverage.toFixed(1)}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2 text-slate-500">
                <Calendar size={16} className="text-amber-500" />
                <span className="text-xs font-medium">Evaluaciones recibidas</span>
              </div>
              <p className="mt-2 text-3xl font-bold text-slate-900">{evaluations.length}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2 text-slate-500">
                <TrendingUp size={16} className="text-amber-500" />
                <span className="text-xs font-medium">Mejor aspecto</span>
              </div>
              <p className="mt-2 text-lg font-bold text-slate-900">
                {bestAspect ? bestAspect.label : '—'}
              </p>
              {bestAspect ? (
                <p className="text-xs text-emerald-600">{bestAspect.avg.toFixed(1)} de promedio</p>
              ) : null}
            </div>
          </div>

          <div>
            <h2 className="mb-3 text-sm font-semibold text-slate-700">Historial de evaluaciones</h2>
            <div className="space-y-3">
              {evaluations.map((ev) => (
                <EvaluationCard key={ev._id} ev={ev} />
              ))}
            </div>
          </div>
        </div>
      )}
    </ColaboradorLayout>
  )
}

export default ColaboradorEvaluaciones
